import React, { useState, useEffect } from 'react';
import { Search, Filter, Clock, Star, BookOpen, Users, TrendingUp } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm">
    <div className="flex items-center gap-4">
      <div className="p-3 bg-navy-50 rounded-lg">
        <Icon className="text-navy-600" size={24} />
      </div>
      <div>
        <p className="text-gray-600 text-sm">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const CourseCard = ({ course }) => {
  // Format duration from interval to readable string
  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    return duration.replace(/(\d+):(\d+):(\d+)/, '$1h $2m');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group">
      <div className="relative">
        <img 
          src={course.thumbnail_url || "/api/placeholder/400/250"} 
          alt={course.title} 
          className="w-full h-48 object-cover rounded-t-xl group-hover:brightness-90 transition-all"
        />
        <div className="absolute top-4 right-4">
          <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-navy-600">
            {course.level}
          </span>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-gray-600 flex items-center gap-1">
            <Clock size={14} />
            {formatDuration(course.duration)}
          </span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-navy-600 transition-colors">
          {course.title}
        </h3>
        <p className="text-gray-600 mb-4 line-clamp-2">{course.description}</p>
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-1">
            <span className="font-medium text-navy-600">{course.price ? `$${course.price}` : 'Free'}</span>
          </div>
          <button className="bg-navy-600 text-white px-6 py-2 rounded-lg hover:bg-navy-700 transition-colors">
            {course.status === 'published' ? 'Enroll Now' : 'Coming Soon'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const stats = [
    { icon: BookOpen, label: 'Total Courses', value: courses.length },
    { icon: Users, label: 'Published Courses', value: courses.filter(c => c.status === 'published').length },
    { icon: TrendingUp, label: 'Free Courses', value: courses.filter(c => !c.price).length }
  ];

  const categories = [
    { id: 'all', name: 'All Courses' },
    { id: 'sales', name: 'Sales Techniques' },
    { id: 'negotiation', name: 'Negotiation' },
    { id: 'leadership', name: 'Leadership' },
    { id: 'communication', name: 'Communication' }
  ];

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const { data, error: supabaseError } = await supabase
          .from('courses')
          .select('*')
          .order('created_at', { ascending: false });

        if (supabaseError) {
          throw supabaseError;
        }

        setCourses(data || []);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching courses:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  // Filter courses based on search and category
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || course.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading courses...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="pl-64 pt-16 bg-gradient-to-b from-gray-50 to-white min-h-screen">
      <div className="max-w-7xl mx-auto p-8">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Course Catalog</h1>
            <p className="text-gray-600">Discover our comprehensive range of courses</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 px-4 py-2 pl-10 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-navy-600/20 focus:border-navy-600 transition-all"
              />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <Filter size={20} />
              Filters
            </button>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>

        {/* Categories Section */}
        <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category.id}
              className={`px-6 py-2.5 rounded-lg whitespace-nowrap transition-all duration-300 font-medium ${
                selectedCategory === category.id
                  ? 'bg-navy-600 text-white shadow-lg shadow-navy-600/20'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map(course => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CoursesPage;